// Утилиты для работы с типами CV записей
// Централизованная конфигурация типов CV записей

import { Briefcase, GraduationCap, BookOpen } from 'lucide-react';

// Основные типы CV записей
const CV_TYPES = {
  EXPERIENCES: 'experiences',
  EDUCATION: 'education',
  COURSES: 'courses'
};

// Массив всех типов для валидации
const ALL_CV_TYPES = Object.values(CV_TYPES);

// Лейблы для отображения в UI
const CV_TYPE_LABELS = {
  [CV_TYPES.EXPERIENCES]: 'Work Experience',
  [CV_TYPES.EDUCATION]: 'Education',
  [CV_TYPES.COURSES]: 'Courses'
};

// Цвета для типов в UI
const CV_TYPE_COLORS = {
  [CV_TYPES.EXPERIENCES]: 'bg-blue-100 text-blue-800',
  [CV_TYPES.EDUCATION]: 'bg-green-100 text-green-800',
  [CV_TYPES.COURSES]: 'bg-purple-100 text-purple-800'
};

// HEX цвета для типов
const CV_TYPE_HEX_COLORS = {
  [CV_TYPES.EXPERIENCES]: '#3B82F6',
  [CV_TYPES.EDUCATION]: '#10B981',
  [CV_TYPES.COURSES]: '#8B5CF6'
};

// Описания типов для помощи пользователю
const CV_TYPE_DESCRIPTIONS = {
  [CV_TYPES.EXPERIENCES]: 'Work experience, internships, projects',
  [CV_TYPES.EDUCATION]: 'Universities, colleges, schools',
  [CV_TYPES.COURSES]: 'Online courses, certifications, trainings'
};

// Плейсхолдеры для полей в зависимости от типа
const CV_TYPE_PLACEHOLDERS = {
  [CV_TYPES.EXPERIENCES]: {
    company_name: 'E.g.: Google, Microsoft',
    specialisation: 'E.g.: Frontend Development, Data Science'
  },
  [CV_TYPES.EDUCATION]: {
    company_name: 'E.g.: MSU, MIT, Stanford',
    specialisation: 'E.g.: Computer Science, Business Administration'
  },
  [CV_TYPES.COURSES]: {
    company_name: 'E.g.: Coursera, Udemy, Yandex Practicum',
    specialisation: 'E.g.: React Development, Machine Learning'
  }
};

// Валидация типа CV записи
const isValidCVType = (type) => {
  return ALL_CV_TYPES.includes(type);
};

// Получение лейбла типа
const getCVTypeLabel = (type) => {
  return CV_TYPE_LABELS[type] || type;
};

// Получение цвета типа для UI
const getCVTypeColor = (type) => {
  return CV_TYPE_COLORS[type] || 'bg-gray-100 text-gray-800';
};

// Получение HEX цвета типа
const getCVTypeHexColor = (type) => {
  return CV_TYPE_HEX_COLORS[type] || '#6B7280';
};

// Получение описания типа
const getCVTypeDescription = (type) => {
  return CV_TYPE_DESCRIPTIONS[type] || '';
};

// Получение плейсхолдеров для полей
const getCVTypePlaceholders = (type) => {
  return CV_TYPE_PLACEHOLDERS[type] || CV_TYPE_PLACEHOLDERS[CV_TYPES.EXPERIENCES];
};

// Получение всех типов для селекта
const getCVTypeOptions = () => {
  return ALL_CV_TYPES.map(type => ({
    value: type,
    label: getCVTypeLabel(type),
    description: getCVTypeDescription(type)
  }));
};

// Получение иконки для типа CV записи
const getCVTypeIcon = (type) => {
  const icons = {
    [CV_TYPES.EXPERIENCES]: Briefcase,
    [CV_TYPES.EDUCATION]: GraduationCap,
    [CV_TYPES.COURSES]: BookOpen
  };
  return icons[type] || Briefcase;
};

// Получение рекомендуемых полей для типа
const getCVTypeRequiredFields = (type) => {
  const requiredFields = {
    [CV_TYPES.EXPERIENCES]: ['company_name', 'start_date', 'specialisation'],
    [CV_TYPES.EDUCATION]: ['company_name', 'start_date', 'specialisation'],
    [CV_TYPES.COURSES]: ['company_name', 'start_date', 'specialisation']
  };
  return requiredFields[type] || requiredFields[CV_TYPES.EXPERIENCES];
};

// Получение рекомендуемых полей для типа
const getCVTypeOptionalFields = (type) => {
  const optionalFields = {
    [CV_TYPES.EXPERIENCES]: ['end_date', 'description', 'country_id'],
    [CV_TYPES.EDUCATION]: ['end_date', 'description', 'country_id'],
    [CV_TYPES.COURSES]: ['end_date', 'description', 'country_id']
  };
  return optionalFields[type] || optionalFields[CV_TYPES.EXPERIENCES];
};

// Форматирование даты для отображения в зависимости от типа
const formatCVDateRange = (startDate, endDate, cvType) => {
  // Если нет startDate, возвращаем пустую строку
  if (!startDate) {
    return '';
  }

  const start = new Date(startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const end = endDate ? new Date(endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Present';

  const labels = {
    [CV_TYPES.EXPERIENCES]: { start: 'Worked from', end: 'to' },
    [CV_TYPES.EDUCATION]: { start: 'Studied from', end: 'to' },
    [CV_TYPES.COURSES]: { start: 'Completed from', end: 'to' }
  };

  const label = labels[cvType] || labels[CV_TYPES.EXPERIENCES];

  return `${label.start} ${start} ${label.end} ${end}`;
};

// Получение заголовка секции для типа
const getCVTypeSectionTitle = (type) => {
  const titles = {
    [CV_TYPES.EXPERIENCES]: 'Work Experience',
    [CV_TYPES.EDUCATION]: 'Education',
    [CV_TYPES.COURSES]: 'Courses & Certifications'
  };
  return titles[type] || 'CV Records';
};

// Группировка CV записей по типам
const groupCVByType = (cvRecords) => {
  return cvRecords.reduce((groups, record) => {
    const type = record.cv_type || CV_TYPES.EXPERIENCES;
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(record);
    return groups;
  }, {});
};

// Сортировка CV записей по дате (новые сначала)
const sortCVByDate = (cvRecords) => {
  return [...cvRecords].sort((a, b) => {
    const dateA = new Date(a.start_date);
    const dateB = new Date(b.start_date);
    return dateB - dateA;
  });
};

// Экспорт констант и утилит
export {
  CV_TYPES,
  ALL_CV_TYPES,
  CV_TYPE_LABELS,
  CV_TYPE_COLORS,
  CV_TYPE_HEX_COLORS,
  CV_TYPE_DESCRIPTIONS,
  CV_TYPE_PLACEHOLDERS,
  isValidCVType,
  getCVTypeLabel,
  getCVTypeColor,
  getCVTypeHexColor,
  getCVTypeDescription,
  getCVTypePlaceholders,
  getCVTypeOptions,
  getCVTypeIcon,
  getCVTypeRequiredFields,
  getCVTypeOptionalFields,
  formatCVDateRange,
  getCVTypeSectionTitle,
  groupCVByType,
  sortCVByDate
};

export default {
  CV_TYPES,
  ALL_CV_TYPES,
  CV_TYPE_LABELS,
  CV_TYPE_COLORS,
  CV_TYPE_HEX_COLORS,
  CV_TYPE_DESCRIPTIONS,
  CV_TYPE_PLACEHOLDERS,
  isValidCVType,
  getCVTypeLabel,
  getCVTypeColor,
  getCVTypeHexColor,
  getCVTypeDescription,
  getCVTypePlaceholders,
  getCVTypeOptions,
  getCVTypeIcon,
  getCVTypeRequiredFields,
  getCVTypeOptionalFields,
  formatCVDateRange,
  getCVTypeSectionTitle,
  groupCVByType,
  sortCVByDate
};
